import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Title,
  TextInput,
  Select,
  Button,
  Stack,
  Group,
  Textarea,
  NumberInput,
  Checkbox,
  Loader,
  Alert,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { IconArrowLeft } from '@tabler/icons-react';
import { useNavigate, useParams } from 'react-router-dom';
import { contractsApi } from '../../../api/contracts';
import { useCreateContract, useUpdateContract, useContract } from '../hooks/useContracts';
import { useContractTemplates, useDefaultTemplate } from '../hooks/useContractTemplates';
import { ContractType, ContractStatus, CreateContractDto } from '../../../api/contracts';
import { getCurrencySymbol } from '../../../utils/currency';
import dayjs from 'dayjs';

export function ContractForm() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  const createContract = useCreateContract();
  const updateContract = useUpdateContract();
  const { data: contract, isLoading: loadingContract } = useContract(id || '');
  const { data: templates } = useContractTemplates();

  const [formData, setFormData] = useState<CreateContractDto>({
    title: '',
    contractNumber: '',
    type: 'SUPPLY_OF_ITEMS',
    supplierName: '',
    supplierId: undefined,
    startDate: undefined,
    endDate: undefined,
    contractValue: 0,
    currency: 'MVR',
    hasPriceCap: false,
    priceCap: undefined,
    priceCapThreshold: 90,
    templateId: undefined,
    status: 'NOT_STARTED',
  });

  const [selectedTemplateType, setSelectedTemplateType] = useState<ContractType | ''>('');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const { data: defaultTemplate } = useDefaultTemplate(selectedTemplateType as ContractType);

  useEffect(() => {
    if (isEdit) return;
    contractsApi
      .getNextContractNumber()
      .then(({ contractNumber }) => {
        setFormData((prev) =>
          prev.contractNumber ? prev : { ...prev, contractNumber }
        );
      })
      .catch(() => {
        // Non-blocking; user can still enter a number manually
      });
  }, [isEdit]);

  useEffect(() => {
    if (contract && isEdit) {
      setFormData({
        title: contract.title,
        contractNumber: contract.contractNumber,
        type: contract.type,
        supplierName: contract.supplierName,
        supplierId: contract.supplierId,
        startDate: contract.startDate ?? undefined,
        endDate: contract.endDate ?? undefined,
        contractValue: contract.contractValue,
        currency: contract.currency || 'MVR',
        hasPriceCap: contract.hasPriceCap,
        priceCap: contract.priceCap,
        priceCapThreshold: contract.priceCapThreshold || 90,
        templateId: contract.templateId,
        status: contract.status,
      });
      if (contract.type) {
        setSelectedTemplateType(contract.type);
      }
    }
  }, [contract, isEdit]);

  useEffect(() => {
    if (defaultTemplate && !formData.templateId) {
      setFormData((prev) => ({ ...prev, templateId: defaultTemplate.id }));
    }
  }, [defaultTemplate]);

  const datesRequired = formData.status !== 'NOT_STARTED';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    const payload: CreateContractDto = {
      ...formData,
      startDate: formData.startDate || undefined,
      endDate: formData.endDate || undefined,
    };

    try {
      if (isEdit && id) {
        const updateData = Object.fromEntries(
          Object.entries(payload).filter(([_, v]) => v !== undefined)
        );
        await updateContract.mutateAsync({ id, data: updateData });
      } else {
        await createContract.mutateAsync(payload);
      }
      navigate('/contracts');
    } catch (error: unknown) {
      const axiosErr = error as { response?: { data?: { error?: string } } };
      const message = axiosErr.response?.data?.error;
      if (message) {
        setSubmitError(message);
      }
    }
  };

  if (loadingContract && isEdit) {
    return (
      <Container size="xl" py="xl">
        <Loader />
      </Container>
    );
  }

  return (
    <Container size="xl" py="xl">
      <Group mb="xl">
        <Button
          variant="subtle"
          leftSection={<IconArrowLeft size={16} />}
          onClick={() => navigate('/contracts')}
        >
          Back
        </Button>
        <Title order={2}>{isEdit ? 'Edit Contract' : 'New Contract'}</Title>
      </Group>

      <form onSubmit={handleSubmit}>
        <Stack gap="md">
          {submitError && (
            <Alert color="red" title="Could not save contract">
              {submitError}
            </Alert>
          )}
          <Paper shadow="sm" p="md">
            <Stack gap="md">
              <TextInput
                label="Contract Title"
                placeholder="Enter contract title"
                value={formData.title}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, title: e.target.value }))
                }
                required
              />

              <TextInput
                label="Contract Number"
                placeholder="e.g. HMH/AGR/2026/0001"
                description={
                  isEdit
                    ? undefined
                    : 'Must be unique. A suggested number is pre-filled; change it if needed.'
                }
                value={formData.contractNumber}
                onChange={(e) => {
                  setSubmitError(null);
                  setFormData((prev) => ({ ...prev, contractNumber: e.target.value }));
                }}
                required
              />

              <Select
                label="Contract Type"
                data={[
                  { value: 'SUPPLY_OF_ITEMS', label: 'Supply of Items' },
                  { value: 'SUPPLY_AND_INSTALLATION', label: 'Supply and Installation' },
                  { value: 'WORKS', label: 'Works' },
                ]}
                value={formData.type}
                onChange={(value) => {
                  setFormData((prev) => ({ ...prev, type: value as ContractType }));
                  setSelectedTemplateType(value as ContractType);
                }}
                required
              />

              <Select
                label="Contract Status"
                data={[
                  { value: 'NOT_STARTED', label: 'Not Started' },
                  { value: 'DRAFT', label: 'Draft' },
                  { value: 'ACTIVE', label: 'Active' },
                  { value: 'EXPIRED', label: 'Expired' },
                  { value: 'TERMINATED', label: 'Terminated' },
                ]}
                value={formData.status || 'NOT_STARTED'}
                onChange={(value) =>
                  setFormData((prev) => ({
                    ...prev,
                    status: (value as ContractStatus) || 'NOT_STARTED',
                  }))
                }
                required
              />

              <TextInput
                label="Supplier Name"
                placeholder="Enter supplier name"
                value={formData.supplierName}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, supplierName: e.target.value }))
                }
                required
              />

              <Group grow>
                <DatePickerInput
                  label="Start Date"
                  value={formData.startDate ? new Date(formData.startDate) : null}
                  onChange={(date) =>
                    setFormData((prev) => ({
                      ...prev,
                      startDate: date?.toISOString(),
                    }))
                  }
                  required={datesRequired}
                  clearable={!datesRequired}
                />
                <DatePickerInput
                  label="End Date"
                  value={formData.endDate ? new Date(formData.endDate) : null}
                  onChange={(date) =>
                    setFormData((prev) => ({
                      ...prev,
                      endDate: date?.toISOString(),
                    }))
                  }
                  required={datesRequired}
                  clearable={!datesRequired}
                />
              </Group>

              <Group grow>
                <NumberInput
                  label="Contract Value"
                  placeholder="Enter contract value"
                  value={formData.contractValue}
                  onChange={(value) =>
                    setFormData((prev) => ({ ...prev, contractValue: Number(value) || 0 }))
                  }
                  min={0}
                  decimalScale={2}
                  prefix={`${getCurrencySymbol(formData.currency || 'MVR')} `}
                  required
                />
                <Select
                  label="Currency"
                  data={[
                    { value: 'MVR', label: 'MVR (Maldivian Rufiyaa)' },
                    { value: 'USD', label: 'USD (US Dollar)' },
                  ]}
                  value={formData.currency || 'MVR'}
                  onChange={(value) =>
                    setFormData((prev) => ({ ...prev, currency: (value as 'MVR' | 'USD') || 'MVR' }))
                  }
                  required
                />
              </Group>

              <Checkbox
                label="Has Price Cap"
                checked={formData.hasPriceCap}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, hasPriceCap: e.currentTarget.checked }))
                }
              />

              {formData.hasPriceCap && (
                <Group grow>
                  <NumberInput
                    label="Price Cap"
                    placeholder="Enter price cap"
                    value={formData.priceCap || 0}
                    onChange={(value) =>
                      setFormData((prev) => ({ ...prev, priceCap: Number(value) || undefined }))
                    }
                    min={0}
                    decimalScale={2}
                    prefix={`${getCurrencySymbol(formData.currency || 'MVR')} `}
                  />
                  <NumberInput
                    label="Price Cap Alert Threshold (%)"
                    placeholder="Enter threshold percentage"
                    value={formData.priceCapThreshold || 90}
                    onChange={(value) =>
                      setFormData((prev) => ({
                        ...prev,
                        priceCapThreshold: Number(value) || 90,
                      }))
                    }
                    min={0}
                    max={100}
                    suffix="%"
                  />
                </Group>
              )}

              {templates && templates.length > 0 && (
                <Select
                  label="Template (Optional)"
                  placeholder="Select a template"
                  data={templates
                    .filter((t) => t.type === formData.type)
                    .map((t) => ({ value: t.id, label: t.name }))}
                  value={formData.templateId || ''}
                  onChange={(value) =>
                    setFormData((prev) => ({ ...prev, templateId: value || undefined }))
                  }
                  clearable
                />
              )}
            </Stack>
          </Paper>

          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => navigate('/contracts')}>
              Cancel
            </Button>
            <Button type="submit" loading={createContract.isPending || updateContract.isPending}>
              {isEdit ? 'Update Contract' : 'Create Contract'}
            </Button>
          </Group>
        </Stack>
      </form>
    </Container>
  );
}

